import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import ShareIcon from '@mui/icons-material/Share';
import { Box, ClickAwayListener, IconButton, Popper, Typography } from '@mui/material';

/**
 * Ultra-light card shown in the grid while the user is in "Layout Mode".
 *
 * Rendered as a plain DOM tree (no MUI Paper/Tooltip/transitions) so that
 * dragging/dropping dozens of them stays buttery smooth. It replaces the real,
 * data-heavy card components while the user is editing the layout; the real
 * cards are used when NOT editing, and the saved layout payload is identical
 * because this component does not change any card ids, widths, types or
 * positions.
 *
 * Sized to fully fill its RGL cell (col-4, col-6, col-8, col-12, …) so the
 * visual footprint during layout-editing matches the real card exactly.
 */

// ──────────────────────────────────────────────────────────────────────────
// Helpers (pure, memo-friendly)
// ──────────────────────────────────────────────────────────────────────────

/** Deterministic hash so every distinct title gets a stable accent color. */
const hashString = (str) => {
	let h = 0;
	const s = String(str || '');
	for (let i = 0; i < s.length; i += 1) {
		h = (h << 5) - h + s.charCodeAt(i);
		h |= 0;
	}
	return Math.abs(h);
};

/** Soft, grid-friendly accent palette (calm, desaturated). */
const ACCENT_PALETTE = [
	{ bar: '#1976d2', soft: 'rgba(25, 118, 210, 0.10)', chip: '#1565c0' },   // blue
	{ bar: '#2e7d32', soft: 'rgba(46, 125, 50, 0.10)', chip: '#2e7d32' },    // green
	{ bar: '#ed6c02', soft: 'rgba(237, 108, 2, 0.10)', chip: '#c65a02' },    // orange
	{ bar: '#9c27b0', soft: 'rgba(156, 39, 176, 0.10)', chip: '#7b1fa2' },   // purple
	{ bar: '#00838f', soft: 'rgba(0, 131, 143, 0.10)', chip: '#00838f' },    // teal
	{ bar: '#d81b60', soft: 'rgba(216, 27, 96, 0.10)', chip: '#ad1457' },    // pink
	{ bar: '#5d4037', soft: 'rgba(93, 64, 55, 0.10)', chip: '#5d4037' },     // brown
	{ bar: '#455a64', soft: 'rgba(69, 90, 100, 0.10)', chip: '#37474f' },    // blue-gray
];

const pickAccent = (key) => ACCENT_PALETTE[hashString(key) % ACCENT_PALETTE.length];

/** Convert a numeric grid width (1..12) into a friendly fraction chip. */
const formatWidthChip = (width) => {
	if (typeof width !== 'number' || !Number.isFinite(width)) return null;
	const map = {
		12: 'Full width',
		9: '¾ width',
		8: '⅔ width',
		6: '½ width',
		4: '⅓ width',
		3: '¼ width',
		2: '⅙ width',
	};
	return map[width] || `col-${width}`;
};

const BAR_BG = 'rgba(0, 0, 0, 0.06)';

/** Default skeleton: text lines + optional bar chart (same as pre–Hospital-variant). */
const renderSkeleton = (width, accent) => {
	const isWide = typeof width === 'number' && width >= 8;
	const lines = (
		<div style={{ display: 'flex', flexDirection: 'column', gap: 6, width: '100%' }}>
			<div style={{ height: 6, width: '92%', backgroundColor: BAR_BG, borderRadius: 3 }} />
			<div style={{ height: 6, width: '74%', backgroundColor: BAR_BG, borderRadius: 3 }} />
			<div style={{ height: 6, width: '58%', backgroundColor: BAR_BG, borderRadius: 3 }} />
		</div>
	);

	if (!isWide) return lines;

	return (
		<div style={{ display: 'flex', flexDirection: 'column', gap: 10, width: '100%' }}>
			{lines}
			<div
				style={{
					display: 'flex',
					alignItems: 'flex-end',
					gap: 6,
					height: 36,
					width: '100%',
					paddingTop: 2,
				}}
			>
				{[0.55, 0.8, 0.4, 0.95, 0.65, 0.3].map((h, i) => (
					<div
						key={i}
						style={{
							flex: 1,
							height: `${Math.round(h * 100)}%`,
							backgroundColor: accent.soft,
							borderRadius: 3,
							borderBottom: `2px solid ${accent.bar}`,
						}}
					/>
				))}
			</div>
		</div>
	);
};

const isWideWidth = (width) => typeof width === 'number' && width >= 8;
const isNarrowWidth = (width) => typeof width === 'number' && width <= 4;

/** Hospital layout-mode: chart / analytics hint */
const renderChartSkeleton = (width, accent) => {
	const wide = isWideWidth(width);
	const narrow = isNarrowWidth(width);
	const nBars = narrow ? 5 : wide ? 8 : 6;
	const heights = [0.35, 0.55, 0.45, 0.7, 0.5, 0.85, 0.4, 0.6];
	return (
		<div style={{ display: 'flex', flexDirection: 'column', gap: 8, width: '100%' }}>
			<div style={{ height: 4, width: '40%', backgroundColor: BAR_BG, borderRadius: 2 }} />
			<div
				style={{
					display: 'flex',
					alignItems: 'flex-end',
					gap: narrow ? 4 : 5,
					height: wide ? 44 : 34,
					width: '100%',
					borderBottom: `1px solid rgba(0,0,0,0.08)`,
					paddingBottom: 2,
					boxSizing: 'border-box',
				}}
			>
				{Array.from({ length: nBars }).map((_, i) => (
					<div
						key={i}
						style={{
							flex: 1,
							height: `${Math.round((heights[i % heights.length] || 0.5) * 100)}%`,
							backgroundColor: accent.soft,
							borderRadius: 2,
							borderBottom: `2px solid ${accent.bar}`,
						}}
					/>
				))}
			</div>
			{!narrow ? (
				<div style={{ display: 'flex', gap: 6, width: '100%' }}>
					<div style={{ flex: 1, height: 3, backgroundColor: BAR_BG, borderRadius: 2 }} />
					<div style={{ flex: 1, height: 3, backgroundColor: BAR_BG, borderRadius: 2 }} />
					<div style={{ width: '18%', height: 3, backgroundColor: BAR_BG, borderRadius: 2 }} />
				</div>
			) : null}
		</div>
	);
};

/** Hospital: table / grid hint */
const renderTableSkeleton = (width, accent) => {
	const cols = isNarrowWidth(width) ? 2 : 3;
	const rows = isNarrowWidth(width) ? 3 : 4;
	const header = (
		<div
			style={{
				display: 'flex',
				gap: 4,
				width: '100%',
				marginBottom: 4,
			}}
		>
			{Array.from({ length: cols }).map((_, i) => (
				<div
					key={`h-${i}`}
					style={{
						flex: 1,
						height: 8,
						backgroundColor: accent.soft,
						borderRadius: 2,
						borderBottom: `1px solid ${accent.bar}`,
					}}
				/>
			))}
		</div>
	);
	const body = Array.from({ length: rows }).map((_, ri) => (
		<div key={`r-${ri}`} style={{ display: 'flex', gap: 4, width: '100%', marginBottom: 3 }}>
			{Array.from({ length: cols }).map((_, ci) => (
				<div
					key={`c-${ri}-${ci}`}
					style={{
						flex: 1,
						height: 6,
						backgroundColor: BAR_BG,
						borderRadius: 2,
						opacity: 0.85 - ri * 0.08,
					}}
				/>
			))}
		</div>
	));
	return (
		<div style={{ width: '100%' }}>
			{header}
			{body}
		</div>
	);
};

/** Hospital: KPI / big-number card hint */
const renderKpiSkeleton = (width, accent) => {
	const narrow = isNarrowWidth(width);
	return (
		<div style={{ display: 'flex', flexDirection: 'column', gap: 8, width: '100%' }}>
			<div
				style={{
					height: narrow ? 28 : 36,
					width: narrow ? '72%' : '48%',
					backgroundColor: accent.soft,
					borderRadius: 6,
					border: `1px solid ${accent.bar}`,
				}}
			/>
			<div style={{ height: 5, width: '88%', backgroundColor: BAR_BG, borderRadius: 3 }} />
			<div style={{ height: 5, width: '62%', backgroundColor: BAR_BG, borderRadius: 3 }} />
			{!narrow ? (
				<div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
					<div style={{ flex: 1, height: 32, backgroundColor: BAR_BG, borderRadius: 4 }} />
					<div style={{ flex: 1, height: 32, backgroundColor: BAR_BG, borderRadius: 4 }} />
				</div>
			) : null}
		</div>
	);
};

/** Hospital: list / row breakdown hint */
const renderListSkeleton = (width, accent) => {
	const n = isNarrowWidth(width) ? 4 : 5;
	const widths = ['88%', '76%', '92%', '68%', '80%'];
	return (
		<div style={{ display: 'flex', flexDirection: 'column', gap: 6, width: '100%' }}>
			{Array.from({ length: n }).map((_, i) => (
				<div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%' }}>
					<div
						style={{
							width: 6,
							height: 4,
							borderRadius: 1,
							backgroundColor: accent.bar,
							opacity: 0.35,
							flexShrink: 0,
						}}
					/>

					<div
						style={{
							height: 6,
							width: widths[i % widths.length],
							backgroundColor: BAR_BG,
							borderRadius: 3,
						}}
					/>
				</div>
			))}
		</div>
	);
};

/** Hospital: category group hint (nested tiles) */
const renderCategorySkeleton = (accent) => (
	<div
		style={{
			width: '100%',
			borderRadius: 8,
			border: `1px dashed rgba(0,0,0,0.12)`,
			backgroundColor: 'rgba(0,0,0,0.02)',
			padding: 8,
			boxSizing: 'border-box',
		}}
	>
		<div style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
			<div
				style={{
					flex: 1,
					height: 22,
					borderRadius: 4,
					backgroundColor: accent.soft,
					border: `1px solid rgba(0,0,0,0.06)`,
				}}
			/>
			<div
				style={{
					flex: 1,
					height: 22,
					borderRadius: 4,
					backgroundColor: accent.soft,
					border: `1px solid rgba(0,0,0,0.06)`,
				}}
			/>
		</div>
		<div
			style={{
				height: 20,
				width: '70%',
				borderRadius: 4,
				backgroundColor: BAR_BG,
			}}
		/>
	</div>
);

const renderSkeletonByVariant = (variant, width, accent) => {
	switch (variant) {
		case 'chart':
			return renderChartSkeleton(width, accent);
		case 'table':
			return renderTableSkeleton(width, accent);
		case 'kpi':
			return renderKpiSkeleton(width, accent);
		case 'list':
			return renderListSkeleton(width, accent);
		case 'category':
			return renderCategorySkeleton(accent);
		case 'default':
		default:
			return renderSkeleton(width, accent);
	}
};

// ──────────────────────────────────────────────────────────────────────────
// Component
// ──────────────────────────────────────────────────────────────────────────

/**
 * "Card" variant — mimics the real dashboard card shell exactly:
 *   • Card-style header with the title on the left (like `Card.js` header).
 *   • Body fills the remaining space and shows the type-specific skeleton.
 *   • No drag-grip dots, no width chip, no move-menu icons — just a clean
 *     shell that matches how the real card looks while data is loading.
 *
 * We render this only when explicitly opted-in via `variant="card"` so other
 * pages keep the existing placeholder design until they are migrated.
 */
/**
 * Layout-mode share control: same copy as `Card.js`, opens on click.
 * `data-no-drag` + stopPropagation keep react-grid-layout from stealing the press
 * (see `DragDropLayout` draggableCancel merge).
 */
const LayoutPreviewShareChip = ({ isSharedWithMe, sharedByName, sharedUsers }) => {
	const [open, setOpen] = useState(false);
	const anchorRef = useRef(null);
	const stopDrag = (e) => {
		e.stopPropagation();
	};
	return (
		<Box sx={{ flex: '0 0 auto', display: 'flex', alignItems: 'flex-start', pt: '1px' }}>
			<IconButton
				ref={anchorRef}
				data-no-drag="true"
				size="small"
				disableFocusRipple
				disableRipple
				onPointerDown={stopDrag}
				onMouseDown={stopDrag}
				onClick={(e) => {
					stopDrag(e);
					setOpen((o) => !o);
				}}
				sx={{
					width: 22,
					height: 21,
					p: 0,
					borderRadius: '50%',
					border: '2px solid #4879f5',
					backgroundColor: '#fff',
					color: '#4879f5',
					transition: 'background-color 0.15s ease, border-color 0.15s ease, color 0.15s ease',
					'&:hover': {
						backgroundColor: 'rgba(72, 121, 245, 0.12)',
						borderColor: '#3568d4',
						color: '#3568d4',
					},
				}}
			>
				<ShareIcon sx={{ width: '16px', height: '14px' }} />
			</IconButton>
			<Popper open={open} anchorEl={anchorRef.current} placement="bottom-start" sx={{ zIndex: 1300 }}>
				<ClickAwayListener onClickAway={() => setOpen(false)}>
					<Box
						data-no-drag="true"
						onPointerDown={stopDrag}
						onMouseDown={stopDrag}
						sx={{
							p: 1.5,
							minWidth: 220,
							maxWidth: 320,
							bgcolor: 'background.paper',
							borderRadius: 1,
							boxShadow: 3,
							mt: 0.5,
						}}
					>
						<Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
							{isSharedWithMe ? 'Shared by' : 'Shared with'}
						</Typography>
						{isSharedWithMe ? (
							<Typography variant="body2" sx={{ mb: 0.5 }}>
								{sharedByName || 'Unknown'}
							</Typography>
						) : sharedUsers.length > 0 ? (
							sharedUsers.map((userName) => (
								<Typography key={userName} variant="body2" sx={{ mb: 0.5 }}>
									- {userName}
								</Typography>
							))
						) : (
							<Typography variant="body2" color="text.secondary">
								Not shared with anyone
							</Typography>
						)}
					</Box>
				</ClickAwayListener>
			</Popper>
		</Box>
	);
};

LayoutPreviewShareChip.propTypes = {
	isSharedWithMe: PropTypes.bool.isRequired,
	sharedByName: PropTypes.string,
	sharedUsers: PropTypes.arrayOf(PropTypes.string).isRequired,
};

LayoutPreviewShareChip.defaultProps = {
	sharedByName: null,
};

const CardVariantPlaceholder = ({ label, width, skeletonVariant, accent, body, sharePanel }) => {
	const isNarrow = typeof width === 'number' && width <= 4;

	return (
		<div
			style={{
				position: 'relative',
				width: '100%',
				height: '100%',
				minHeight: 0,
				display: 'flex',
				flexDirection: 'column',
				borderRadius: 8,
				backgroundColor: '#fff',
				boxShadow: '0px 4px 14px 0px rgb(0 0 0 / 6%)',
				overflow: 'hidden',
				userSelect: 'none',
				boxSizing: 'border-box',
			}}
		>
			{/* Header — same paddings/border as the real dashboard card */}
			<div
				style={{
					padding: '12px 10px',
					borderBottom: '1px solid #f6f6f9',
					color: '#444652',
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'space-between',
					gap: 10,
					minHeight: 44,
					boxSizing: 'border-box',
				}}
			>
				<div
					title={label}
					style={{
						flex: '1 1 auto',
						minWidth: 0,
						fontSize: '0.8rem',
						fontWeight: 700,
						lineHeight: 1.3,
						color: '#444652',
						whiteSpace: 'nowrap',
						overflow: 'hidden',
						textOverflow: 'ellipsis',
						maxWidth: sharePanel ? 'calc(100% - 32px)' : '100%',
						fontFamily:
							'"Manrope", "Roboto", "Helvetica", "Arial", sans-serif',
					}}
				>
					{label}
				</div>
				{sharePanel ? <LayoutPreviewShareChip {...sharePanel} /> : null}
			</div>

			{/* Body — caller-supplied dummy UI when `body` is provided, otherwise
			    the generic per-type skeleton preview. Keeping this flexible lets
			    each page swap in a card-specific dummy (reusing existing
			    `*Skeleton` components) to get a pixel-matching preview in layout
			    mode without pulling in the real card's redux / data pipeline. */}
			<div
				style={{
					flex: 1,
					minHeight: 0,
					width: '100%',
					display: 'flex',
					flexDirection: 'column',
					justifyContent: body ? 'flex-start' : 'center',
					padding: body ? 0 : (isNarrow ? '12px 14px' : '14px 18px'),
					boxSizing: 'border-box',
					overflow: 'hidden',
				}}
			>
				{body != null
					? body
					: renderSkeletonByVariant(skeletonVariant ?? 'default', width, accent)}
			</div>
		</div>
	);
};

CardVariantPlaceholder.propTypes = {
	label: PropTypes.string.isRequired,
	width: PropTypes.number,
	skeletonVariant: PropTypes.string,
	accent: PropTypes.shape({
		bar: PropTypes.string,
		soft: PropTypes.string,
		chip: PropTypes.string,
	}).isRequired,
	body: PropTypes.node,
	sharePanel: PropTypes.shape({
		isSharedWithMe: PropTypes.bool,
		sharedByName: PropTypes.string,
		sharedUsers: PropTypes.arrayOf(PropTypes.string),
	}),
};

CardVariantPlaceholder.defaultProps = {
	body: null,
	sharePanel: null,
};

const LayoutPlaceholderCardImpl = ({
	title,
	subtitle,
	headerActions,
	width,
	/** When `variant === "card"`, optional share chip + popper (dynamic custom tabs). */
	sharePanel = null,
	/** When set (Hospital layout mode), picks a tiny type-specific skeleton. */
	skeletonVariant,
	/**
	 * Visual flavor of the placeholder.
	 *   • `"default"` — original tile look (drag grip, body title, width chip, optional actions).
	 *   • `"card"`    — dashboard-card look: clean header with the card title, type-specific
	 *                    skeleton body, and no drag-grip / width-chip / header actions.
	 *
	 * Added as an opt-in so we can roll this out page-by-page without changing any of the
	 * existing pages' behavior until they are migrated explicitly.
	 */
	variant,
	/**
	 * Only used when `variant === "card"`. If provided, this React node replaces the
	 * generic per-type skeleton inside the card shell. Designed for pages that want a
	 * per-card-type dummy body (e.g. reusing the existing `*Skeleton.jsx` components)
	 * so the layout-mode preview matches the real card pixel-for-pixel without pulling
	 * in redux/data dependencies.
	 */
	body,
}) => {
	const label = (title || '').toString().trim() || 'Untitled card';
	const accent = pickAccent(label);

	if (variant === 'card') {
		return (
			<CardVariantPlaceholder
				label={label}
				width={width}
				skeletonVariant={skeletonVariant}
				accent={accent}
				body={body}
				sharePanel={sharePanel}
			/>
		);
	}

	const widthChip = formatWidthChip(width);
	const isNarrow = typeof width === 'number' && width <= 4;
	const isWide = typeof width === 'number' && width >= 8;

	return (
		<div
			style={{
				position: 'relative',
				width: '100%',
				height: '100%',
				minHeight: 0,
				display: 'flex',
				flexDirection: 'column',
				borderRadius: 12,
				border: '1px solid rgba(0, 0, 0, 0.10)',
				backgroundColor: '#fff',
				boxShadow: '0 1px 3px rgba(0, 0, 0, 0.06)',
				overflow: 'hidden',
				userSelect: 'none',
				boxSizing: 'border-box',
			}}
		>
			{/* Left accent bar — stable color per title gives each card its identity */}
			<div
				style={{
					position: 'absolute',
					top: 0,
					bottom: 0,
					left: 0,
					width: 4,
					backgroundColor: accent.bar,
				}}
			/>

			{/* Header: drag-grip affordance + optional actions */}
			<div
				style={{
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'space-between',
					paddingLeft: 14,
					paddingRight: 8,
					paddingTop: 8,
					minHeight: 28,
				}}
			>
				<div
					aria-hidden
					style={{
						display: 'grid',
						gridTemplateColumns: 'repeat(2, 3px)',
						gridTemplateRows: 'repeat(3, 3px)',
						columnGap: 3,
						rowGap: 3,
					}}
				>
					{Array.from({ length: 6 }).map((_, i) => (
						<span
							key={i}
							style={{
								width: 3,
								height: 3,
								borderRadius: '50%',
								backgroundColor: 'rgba(0, 0, 0, 0.22)',
							}}
						/>
					))}
				</div>

				{headerActions ? (
					<div
						onMouseDown={(e) => e.stopPropagation()}
						style={{
							display: 'inline-flex',
							alignItems: 'center',
							gap: 4,
						}}
					>
						{headerActions}
					</div>
				) : null}
			</div>

			{/* Body: optional meta eyebrow, title, skeleton preview */}
			<div
				style={{
					flex: 1,
					minHeight: 0,
					display: 'flex',
					flexDirection: 'column',
					justifyContent: 'center',
					gap: 10,
					padding: isNarrow ? '4px 14px 10px 18px' : '6px 18px 12px 22px',
				}}
			>
				<div style={{ minWidth: 0 }}>
					{subtitle ? (
						<div
							title={subtitle}
							style={{
								marginBottom: 5,
								fontSize: 10,
								fontWeight: 600,
								letterSpacing: '0.06em',
								textTransform: 'uppercase',
								color: 'rgba(0, 0, 0, 0.45)',
								whiteSpace: 'nowrap',
								overflow: 'hidden',
								textOverflow: 'ellipsis',
							}}
						>
							{subtitle}
						</div>
					) : null}
					<div
						title={label}
						style={{
							fontSize: isNarrow ? 14 : isWide ? 17 : 15,
							fontWeight: 700,
							lineHeight: 1.28,
							letterSpacing: '-0.02em',
							color: '#0d1117',
							wordBreak: 'break-word',
							hyphens: 'auto',
							display: '-webkit-box',
							WebkitLineClamp: isNarrow ? 2 : 3,
							WebkitBoxOrient: 'vertical',
							overflow: 'hidden',
						}}
					>
						{label}
					</div>
				</div>

				{renderSkeletonByVariant(skeletonVariant ?? 'default', width, accent)}
			</div>

			{/* Footer: width chip */}
			{widthChip ? (
				<div
					style={{
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'flex-start',
						padding: '0 14px 10px 18px',
					}}
				>
					<span
						style={{
							display: 'inline-flex',
							alignItems: 'center',
							fontSize: 10,
							fontWeight: 600,
							letterSpacing: '0.03em',
							textTransform: 'uppercase',
							color: accent.chip,
							backgroundColor: accent.soft,
							padding: '3px 8px',
							borderRadius: 999,
						}}
					>
						{widthChip}
					</span>
				</div>
			) : null}
		</div>
	);
};

LayoutPlaceholderCardImpl.propTypes = {
	title: PropTypes.string,
	subtitle: PropTypes.string,
	headerActions: PropTypes.node,
	width: PropTypes.number,
	sharePanel: PropTypes.shape({
		isSharedWithMe: PropTypes.bool,
		sharedByName: PropTypes.string,
		sharedUsers: PropTypes.arrayOf(PropTypes.string),
	}),
	skeletonVariant: PropTypes.oneOf([
		'default',
		'chart',
		'table',
		'kpi',
		'list',
		'category',
	]),
	variant: PropTypes.oneOf(['default', 'card']),
	body: PropTypes.node,
};

LayoutPlaceholderCardImpl.defaultProps = {
	variant: 'default',
	body: null,
	sharePanel: null,
};

const LayoutPlaceholderCard = React.memo(LayoutPlaceholderCardImpl);

export default LayoutPlaceholderCard;
